import { useEffect, useState } from "preact/hooks";
import { Panel } from "../Panel/Panel.jsx";
import "./ThresholdMeter.css";

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function createSegments(range) {
  const { min, good, target, caution, max } = range;
  const width = max - min;

  if (!(min < good && good < target && target < caution && caution < max)) {
    throw new Error("ThresholdMeter range values must be strictly ascending.");
  }

  return [
    { tone: "good", width: `${((good - min) / width) * 100}%` },
    { tone: "expected", width: `${((target - good) / width) * 100}%` },
    { tone: "caution", width: `${((caution - target) / width) * 100}%` },
    { tone: "danger", width: `${((max - caution) / width) * 100}%` }
  ];
}

function markerOffset(value, range) {
  const boundedValue = clamp(value, range.min, range.max);
  return `${((boundedValue - range.min) / (range.max - range.min)) * 100}%`;
}

function markerPositionPercent(value, range) {
  const boundedValue = clamp(value, range.min, range.max);
  return ((boundedValue - range.min) / (range.max - range.min)) * 100;
}

function thresholdTone(value, range) {
  const boundedValue = clamp(value, range.min, range.max);

  if (boundedValue < range.good) {
    return "good";
  }

  if (boundedValue < range.target) {
    return "expected";
  }

  if (boundedValue < range.caution) {
    return "caution";
  }

  return "danger";
}

export function ThresholdMeterBar({ label, value, range, startValue = null }) {
  const segments = createSegments(range);
  const [animatedValue, setAnimatedValue] = useState(() => (
    startValue === null ? value : startValue
  ));
  const marker = markerOffset(animatedValue, range);
  const targetMarkerPosition = markerPositionPercent(value, range);
  const useEdgeSafeMotion = targetMarkerPosition <= 10 || targetMarkerPosition >= 90;

  useEffect(() => {
    if (startValue === null) {
      setAnimatedValue(value);
      return undefined;
    }

    setAnimatedValue(startValue);
    const timeoutId = window.setTimeout(() => {
      setAnimatedValue(value);
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [startValue, value]);

  return (
    <div class="threshold-meter">
      <div class="threshold-meter-track">
        {segments.map((segment) => (
          <span
            key={`${label}-${segment.tone}`}
            class={`threshold-meter-segment tone-${segment.tone}`}
            style={{ width: segment.width }}
          />
        ))}
        <span
          class={`threshold-meter-marker${useEdgeSafeMotion ? " threshold-meter-marker--edge-safe" : ""}`}
          style={{ insetInlineStart: `calc(${marker} - 0.1rem)` }}
        />
      </div>
    </div>
  );
}

export function ThresholdMeter({ label, value, valueLabel, range, startValue = null }) {
  const tone = thresholdTone(value, range);

  return (
    <Panel
      className="threshold-meter-panel"
      header={label}
      headerEnd={<span class={`threshold-meter-value tone-${tone}`}>{valueLabel}</span>}
      padded
    >
      <ThresholdMeterBar
        label={label}
        value={value}
        range={range}
        startValue={startValue}
      />
    </Panel>
  );
}
